import { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';

import AddAllocationFormLayout from '../../../shared/components/AddAllocationFormLayout/AddAllocationFormLayout';
import { AllocationMode } from '../../../constants/allocationMode';
import { useAddClientAllocationContext } from '../../context/AddClientAllocationContext';
import useClientAllocationRange from '../../../shared/hooks/useClientAllocationRange';
import { IAddAllocationFormValues } from '../../types';
import useAccordionPanel from '../../../shared/hooks/useAccordionPanel';
import AllocationMembersList from '../../../shared/components/AllocationMembersList/AllocationMembersList';
import AssignedClients from '../AssignedClients';
import EditAllocationFormActions from '../../../shared/components/EditAllocationFormActions/EditAllocationFormActions';
import { LEAVE_PAGE_DESCRIPTION } from '../../../shared/constants/allocationMessages';
import { NETWORK } from '~/ui/constants/paths';
import { useStoreState } from '~/store/hooks';

interface AddAllocationFormComponentProps {
  isSubmitting: boolean;
  isRestored: boolean;
  clearDraft: VoidFunction;
}

const AddAllocationFormComponent = ({
  isSubmitting,
  isRestored,
  clearDraft,
}: AddAllocationFormComponentProps): ReactElement => {
  const navigate = useNavigate();

  const {
    watch,
    formState: { errors, isDirty },
  } = useFormContext<IAddAllocationFormValues>();
  const [teamMembersAllocations, dateRange] = watch(['teamMembersAllocations', 'dateRange']);
  const { dateAvailability } = useStoreState(state => state.clientAllocation);

  const { teamMembers, clinicId, allocationMode, actTeamId } = useAddClientAllocationContext();
  const isLocationGroupsMode = allocationMode === AllocationMode.BY_LOCATION_GROUP;

  const { datesValidation } = useClientAllocationRange({ clinicId, actTeamId });

  const { expandedPanelIds, handleToggle } = useAccordionPanel();

  const hasAllocationConflict = teamMembersAllocations.some(member => {
    const lgClientIdSet = new Set(
      Object.keys(member.selectedLocationGroupClientIds ?? {}).map(Number),
    );
    return (member.selectedClientsIds ?? []).some(id => lgClientIdSet.has(id));
  });

  const isSaveDisabled =
    !dateRange[0] ||
    !dateRange[1] ||
    Object.keys(errors).length > 0 ||
    !isDirty ||
    isSubmitting ||
    hasAllocationConflict ||
    (!!dateAvailability?.clients.length &&
      dateAvailability?.clients.some(client => !client.canBeCreated));

  return (
    <>
      <AddAllocationFormLayout
        title={
          isLocationGroupsMode
            ? 'Location Groups per Team Member'
            : 'Assigned Clients per Team Member'
        }
        isRestored={isRestored}
        datesValidation={datesValidation}
      >
        <AllocationMembersList
          teamMembersAllocations={teamMembersAllocations}
          clinicId={clinicId}
          expandedPanelIds={expandedPanelIds}
          isLocationGroupsMode={isLocationGroupsMode}
          datesValidation={datesValidation}
          onToggle={handleToggle}
          renderMemberContent={teamMemberIdx => (
            <AssignedClients
              teamMemberIdx={teamMemberIdx}
              teamMember={teamMembers[teamMemberIdx]}
            />
          )}
        />
      </AddAllocationFormLayout>
      <EditAllocationFormActions
        onCancel={() => navigate(NETWORK)}
        isSaveDisabled={isSaveDisabled}
        leavePageWhen={isDirty && !isSubmitting}
        leavePageDescription={LEAVE_PAGE_DESCRIPTION}
        withClearDraft
        onClearDraft={clearDraft}
      />
    </>
  );
};

export default AddAllocationFormComponent;
